<script setup>
// 裁剪图片工具（nuxt#19 P3）。复刻 design "Crop Image.html" 内联 <script> 的 crop 逻辑：
// 上传 → canvas 预览 + 可拖动/缩放的裁剪框（支持 Free/1:1/4:3/16:9/9:16 比例）→ Apply & Download 裁剪后图片。
// 纯浏览器（Canvas + FileReader + URL.createObjectURL），文件不上传。复刻原 cropRect/fitRatio/drag/resize/applyCrop。
import { ref, computed, nextTick, onBeforeUnmount } from "vue";
import { fileTooLargeMsg, looksLikeEditableImage } from "@/settings/free-tools/batch-limits.js";
import { useCanvasDecode } from "@/composables/useCanvasDecode";

const props = defineProps({
  mode: { type: String, default: "crop" }, // 单工具，mode 备用
  slug: { type: String, default: "" },
});

const { downloadBlob } = useFreeToolsDownload();
const { decodeForCanvas, decodeErrorMsg } = useCanvasDecode();

const RATIOS = [
  { r: "free", label: "Free", v: 0 },
  { r: "1:1", label: "1:1", v: 1 },
  { r: "4:3", label: "4:3", v: 4 / 3 },
  { r: "16:9", label: "16:9", v: 16 / 9 },
  { r: "9:16", label: "9:16", v: 9 / 16 },
];

const stage = ref("drop"); // drop | work
const dragging = ref(false);
const fileName = ref("image.png");
const fileInput = ref(null);
const canvas = ref(null); // 显示用 canvas
const cropLayer = ref(null); // 覆盖层（与 canvas 同尺寸，承载 crop-rect）
const thumbUrl = ref("");
const activeRatio = ref("free");
const cropRatio = ref(0); // 0 = free，否则 w/h

// 裁剪框（百分比，对应原 cropRect.style.left/top/width/height）
const box = ref({ left: 12, top: 12, width: 60, height: 60 });

// 源图绘制到离屏 work canvas（保持原始像素，裁剪基于它）
let work = null;
let baseName = "image";
let srcType = "image/png";
let lastUrl = "";

const cropReadout = computed(() => {
  if (!work) return "Drag the box to set the crop";
  const w = Math.round((work.width * box.value.width) / 100);
  const h = Math.round((work.height * box.value.height) / 100);
  return w + " × " + h + " px";
});
const boxStyle = computed(() => ({
  left: box.value.left + "%",
  top: box.value.top + "%",
  width: box.value.width + "%",
  height: box.value.height + "%",
}));

function fmtType() {
  return srcType === "image/jpeg" ? "image/jpeg" : srcType === "image/webp" ? "image/webp" : "image/png";
}

function paint() {
  const c = canvas.value;
  if (!c || !work) return;
  c.width = work.width;
  c.height = work.height;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.drawImage(work, 0, 0);
}

function revoke() {
  if (lastUrl) { URL.revokeObjectURL(lastUrl); lastUrl = ""; }
}

async function loadFile(file) {
  if (!file || !looksLikeEditableImage(file)) { alert("Please choose an image file."); return; }
  const _m = fileTooLargeMsg(file, "image");
  if (_m) { alert(_m); return; }
  baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  srcType = file.type;
  fileName.value = file.name;
  // 浏览器解不了的源(heic/tiff/tga/dds/psd…)先经后端转 png,再进 canvas(用户无感)。命名仍用原 file。
  let _f;
  try { _f = await decodeForCanvas(file); }
  catch (e) { alert(decodeErrorMsg(file)); return; }
  revoke();
  const url = URL.createObjectURL(_f);
  lastUrl = url;
  const im = new Image();
  im.onload = async () => {
    work = document.createElement("canvas");
    work.width = im.naturalWidth;
    work.height = im.naturalHeight;
    work.getContext("2d").drawImage(im, 0, 0);
    thumbUrl.value = url;
    stage.value = "work";
    activeRatio.value = "free";
    cropRatio.value = 0;
    await nextTick();
    resetCropBox();
    paint();
  };
  im.onerror = () => { alert("Could not load this image."); };
  im.src = url;
}

/* ---- 拖放 / 选择 ---- */
function pick() { fileInput.value && fileInput.value.click(); }
function onInput(e) { const f = e.target.files && e.target.files[0]; if (f) loadFile(f); }
function onDrop(e) { e.preventDefault(); dragging.value = false; const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) loadFile(f); }

function reset() {
  stage.value = "drop";
  work = null;
  thumbUrl.value = "";
  if (fileInput.value) fileInput.value.value = "";
}

/* ---- 裁剪框：比例 / 复位 / 适配（复刻原 resetCropBox + fitRatio） ---- */
function resetCropBox() {
  box.value = { left: 12, top: 12, width: 60, height: 60 };
  if (cropRatio.value) fitRatio();
}
function setRatio(item) {
  activeRatio.value = item.r;
  cropRatio.value = item.v;
  if (item.v) fitRatio();
}
function fitRatio() {
  const lay = cropLayer.value;
  if (!lay) return;
  const layW = lay.clientWidth, layH = lay.clientHeight;
  if (!layW || !layH) return;
  let w = (box.value.width / 100) * layW;
  let h = w / cropRatio.value;
  if (h > layH * 0.92) { h = layH * 0.92; w = h * cropRatio.value; }
  let nw = (w / layW) * 100;
  let nh = (h / layH) * 100;
  let nl = box.value.left, nt = box.value.top;
  if (nl + nw > 100) nl = 100 - nw;
  if (nt + nh > 100) nt = 100 - nh;
  box.value = { left: Math.max(0, nl), top: Math.max(0, nt), width: nw, height: nh };
}

/* ---- 拖动整框（复刻原 cropRect mousedown move） ---- */
function onBoxDown(e) {
  e.preventDefault();
  const lay = cropLayer.value;
  if (!lay) return;
  const r = lay.getBoundingClientRect();
  const sx = e.clientX, sy = e.clientY;
  const ox = box.value.left, oy = box.value.top;
  const w = box.value.width, h = box.value.height;
  const mv = (ev) => {
    let nx = ox + ((ev.clientX - sx) / r.width) * 100;
    let ny = oy + ((ev.clientY - sy) / r.height) * 100;
    nx = Math.max(0, Math.min(100 - w, nx));
    ny = Math.max(0, Math.min(100 - h, ny));
    box.value = { ...box.value, left: nx, top: ny };
  };
  const up = () => { document.removeEventListener("mousemove", mv); document.removeEventListener("mouseup", up); };
  document.addEventListener("mousemove", mv);
  document.addEventListener("mouseup", up);
}

/* ---- 角点缩放（复刻原 i.ch mousedown move） ---- */
function onHandleDown(e, cls) {
  e.preventDefault();
  e.stopPropagation();
  const lay = cropLayer.value;
  if (!lay) return;
  const r = lay.getBoundingClientRect();
  const right = cls.includes("tr") || cls.includes("br");
  const bottom = cls.includes("bl") || cls.includes("br");
  const L = box.value.left, T = box.value.top, W = box.value.width, H = box.value.height;
  const mv = (ev) => {
    let px = ((ev.clientX - r.left) / r.width) * 100;
    let py = ((ev.clientY - r.top) / r.height) * 100;
    let nl = L, nt = T, nw = W, nh = H;
    if (right) nw = Math.max(8, Math.min(100 - L, px - L));
    else { nl = Math.min(L + W - 8, Math.max(0, px)); nw = W + (L - nl); }
    if (cropRatio.value) {
      nh = (nw / cropRatio.value) * (r.width / r.height);
      if (bottom) { /* 下边角：高度向下延伸，top 不变 */ }
      else { nt = T + H - nh; }
    } else {
      if (bottom) nh = Math.max(8, Math.min(100 - T, py - T));
      else { nt = Math.min(T + H - 8, Math.max(0, py)); nh = H + (T - nt); }
    }
    nt = Math.max(0, Math.min(100 - nh, nt));
    nl = Math.max(0, Math.min(100 - nw, nl));
    box.value = { left: nl, top: nt, width: nw, height: nh };
  };
  const up = () => { document.removeEventListener("mousemove", mv); document.removeEventListener("mouseup", up); };
  document.addEventListener("mousemove", mv);
  document.addEventListener("mouseup", up);
}

/* ---- 应用裁剪 + 下载（复刻原 applyCrop + downloadBtn） ---- */
function makeCropped() {
  if (!work) return null;
  const sx = (box.value.left / 100) * work.width;
  const sy = (box.value.top / 100) * work.height;
  const sw = (box.value.width / 100) * work.width;
  const sh = (box.value.height / 100) * work.height;
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(sw));
  c.height = Math.max(1, Math.round(sh));
  c.getContext("2d").drawImage(work, sx, sy, sw, sh, 0, 0, c.width, c.height);
  return c;
}
async function applyCrop() {
  const c = makeCropped();
  if (!c) return;
  // 把裁剪结果回填为新的 work，便于继续裁剪/再次下载（复刻原 work=c）
  work = c;
  activeRatio.value = "free";
  cropRatio.value = 0;
  await nextTick();
  resetCropBox();
  paint();
}
function download() {
  const c = makeCropped();
  if (!c) return;
  const tt = fmtType();
  const ext = tt === "image/jpeg" ? "jpg" : tt === "image/webp" ? "webp" : "png";
  c.toBlob((b) => downloadBlob(b, baseName + "-cropped." + ext), tt, 0.95);
}

onBeforeUnmount(revoke);
</script>

<template>
  <div class="crop-tool">
    <input ref="fileInput" type="file" accept="image/*" hidden @change="onInput" />

    <!-- 拖放区 -->
    <div v-show="stage === 'drop'" class="dropzone" :class="{ drag: dragging }"
      @click="pick" @dragenter.prevent="dragging = true" @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false" @drop="onDrop">
      <div class="dz-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2v14a2 2 0 002 2h14M2 6h14a2 2 0 012 2v14" /></svg></div>
      <h3>Drop your image here</h3>
      <div class="hint">or click to browse — PNG, JPG, WEBP, GIF</div>
      <div class="formats"><span>PNG</span><span>JPG</span><span>WEBP</span><span>GIF</span></div>
      <div class="dz-or">Cropped right in your browser — never stored</div>
      <button class="btn btn-amber dz-btn" type="button" @click.stop="pick">Choose Image</button>
    </div>

    <!-- 工作区 -->
    <div v-if="stage === 'work'" class="workspace show">
      <div class="ws-grid">
        <div class="ws-preview">
          <div class="cropwrap">
            <canvas ref="canvas"></canvas>
            <div ref="cropLayer" class="crop-layer">
              <div class="crop-rect" :style="boxStyle" @mousedown="onBoxDown">
                <span class="gl cv1"></span><span class="gl cv2"></span>
                <span class="gl ch1"></span><span class="gl ch2"></span>
                <i class="ch tl" @mousedown="onHandleDown($event, 'tl')"></i>
                <i class="ch tr" @mousedown="onHandleDown($event, 'tr')"></i>
                <i class="ch bl" @mousedown="onHandleDown($event, 'bl')"></i>
                <i class="ch br" @mousedown="onHandleDown($event, 'br')"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="ws-panel">
          <div class="ws-file">
            <img class="thumb" :src="thumbUrl" alt="" />
            <div class="meta">
              <div class="fn">{{ fileName }}</div>
              <div class="fsz">{{ cropReadout }}</div>
            </div>
            <span class="rm" title="Remove" @click="reset"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></span>
          </div>

          <div class="ctl">
            <label>Aspect ratio</label>
            <div class="ratios">
              <button v-for="item in RATIOS" :key="item.r" type="button"
                :class="{ active: activeRatio === item.r }" @click="setRatio(item)">{{ item.label }}</button>
            </div>
          </div>

          <div class="dim-readout">{{ cropReadout }}</div>

          <div class="ws-actions">
            <button class="btn btn-amber btn-lg" type="button" @click="download">Apply &amp; Download</button>
            <button class="btn btn-outline" type="button" @click="applyCrop">Apply crop (keep editing)</button>
            <button class="btn btn-outline" type="button" @click="reset">Crop another image</button>
          </div>
        </div>
      </div>
    </div>

    <div class="trust">
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>Private — files stay on your device</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>Live canvas preview</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Unlimited &amp; free</span>
    </div>
  </div>
</template>

<style scoped>
.crop-tool { width: 100%; }
.ws-grid { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); }
.ws-preview { position: relative; background: repeating-conic-gradient(#0c0e13 0 25%, #0a0b10 0 50%) 50% / 26px 26px; min-height: 340px; display: grid; place-items: center; padding: 24px; overflow: hidden; }
.cropwrap { position: relative; display: inline-block; line-height: 0; max-width: 100%; }
.cropwrap canvas { max-height: 380px; width: auto; max-width: 100%; border-radius: 8px; box-shadow: 0 18px 50px -18px rgba(0, 0, 0, 0.85); }
.crop-layer { position: absolute; inset: 0; }
.crop-rect { position: absolute; border: 2px solid var(--c-free); box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55); cursor: move; }
.crop-rect .gl { position: absolute; background: rgba(251, 191, 36, 0.5); }
.crop-rect .cv1 { left: 33.3%; top: 0; bottom: 0; width: 1px; }
.crop-rect .cv2 { left: 66.6%; top: 0; bottom: 0; width: 1px; }
.crop-rect .ch1 { top: 33.3%; left: 0; right: 0; height: 1px; }
.crop-rect .ch2 { top: 66.6%; left: 0; right: 0; height: 1px; }
.crop-rect i.ch { position: absolute; width: 14px; height: 14px; background: var(--c-free); border-radius: 3px; border: 2px solid #1a1205; }
.crop-rect i.tl { left: -8px; top: -8px; cursor: nwse-resize; }
.crop-rect i.tr { right: -8px; top: -8px; cursor: nesw-resize; }
.crop-rect i.bl { left: -8px; bottom: -8px; cursor: nesw-resize; }
.crop-rect i.br { right: -8px; bottom: -8px; cursor: nwse-resize; }
.ws-panel { border-left: 1px solid var(--line); padding: 26px; display: flex; flex-direction: column; gap: 20px; }
.ws-file { display: flex; align-items: center; gap: 13px; padding-bottom: 18px; border-bottom: 1px solid var(--line); }
.ws-file .thumb { width: 46px; height: 46px; border-radius: 11px; object-fit: cover; flex: none; background: var(--surface-2); }
.ws-file .meta { min-width: 0; flex: 1; }
.ws-file .fn { font-weight: 700; font-size: 14.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ws-file .fsz { font-size: 12.5px; color: var(--tx-faint); font-weight: 600; margin-top: 2px; }
.ws-file .rm { color: var(--tx-faint); cursor: pointer; flex: none; padding: 6px; border-radius: 8px; transition: 0.15s; }
.ws-file .rm:hover { color: var(--c-image); background: rgba(255, 255, 255, 0.05); }
.ctl { display: flex; flex-direction: column; gap: 9px; }
.ctl label { font-size: 13px; font-weight: 700; color: var(--tx-dim); letter-spacing: 0.02em; }
.ratios { display: flex; flex-wrap: wrap; gap: 7px; }
.ratios button { padding: 9px 13px; border-radius: 9px; border: 1px solid var(--line); background: var(--surface-2); color: var(--tx-dim); font-family: var(--font-b); font-weight: 700; font-size: 13px; cursor: pointer; transition: 0.15s; }
.ratios button:hover { color: var(--tx); }
.ratios button.active { background: color-mix(in oklab, var(--c-free) 16%, transparent); border-color: var(--c-free); color: #f4cd76; }
.dim-readout { font-size: 12.5px; color: var(--tx-faint); font-weight: 600; text-align: center; }
.ws-actions { margin-top: auto; display: flex; flex-direction: column; gap: 10px; }
.ws-actions .btn { white-space: normal; text-align: center; line-height: 1.25; }
.trust { display: flex; align-items: center; justify-content: center; gap: 30px; flex-wrap: wrap; margin: 30px auto 0; color: var(--tx-faint); font-size: 13.5px; font-weight: 600; }
.trust .i { display: flex; align-items: center; gap: 8px; }
.trust .i svg { width: 16px; height: 16px; color: var(--c-gif); }
@media (max-width: 760px) { .ws-grid { grid-template-columns: 1fr; } .ws-panel { border-left: none; border-top: 1px solid var(--line); } }
</style>
